import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, renderList as _renderList, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "bg-white rounded-lg shadow-xl w-full max-w-md p-6" }
const _hoisted_2 = {
  key: 0,
  class: "text-sm text-red-600 mt-2"
}
const _hoisted_3 = { class: "mt-5 flex justify-end gap-2" }
const _hoisted_4 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_5 = {
  key: 0,
  class: "divide-y mb-4",
  "data-test": "members"
}
const _hoisted_6 = { class: "font-medium" }
const _hoisted_7 = {
  key: 0,
  class: "rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
}
const _hoisted_8 = { class: "text-gray-500" }
const _hoisted_9 = {
  key: 1,
  class: "text-sm text-red-600 mt-2"
}
const _hoisted_10 = { class: "mt-5 flex justify-between gap-2" }

import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import { usePhoneInput } from '@/composables/usePhoneInput'
import { resellerName, resellerPath } from '@/config/nav/resellers'
import api from '@/store/api'

// BOT-1610. Step one creates the reseller from a name, a phone, or both (a
// phone makes that person the primary member). Step two adds team members
// against the reseller just created and finally opens its page.

export default {
  __name: 'AddResellerModal',
  props: { open: { type: Boolean, required: true } },
  emits: ['close', 'created'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const router = useRouter()

const step = ref('reseller')
const name = ref('')
const created = ref(null)
const memberName = ref('')
const error = ref('')
const saving = ref(false)

const resellerPhone = usePhoneInput(computed(() => props.open && step.value === 'reseller'))
const memberPhone = usePhoneInput(computed(() => props.open && step.value === 'members'))
const resellerPhoneBox = resellerPhone.container
const memberPhoneBox = memberPhone.container

const canCreate = computed(() => name.value.trim().length > 0 || resellerPhone.valid.value)
const createdName = computed(() => (created.value ? resellerName(created.value) : ''))
const members = computed(() => created.value?.attributes.members ?? [])

const serverError = (e, fallback) => e?.response?.data?.errors?.[0] || fallback

const createReseller = async () => {
  if (!canCreate.value || saving.value) return
  error.value = ''
  saving.value = true
  try {
    const body = {}
    if (name.value.trim()) body.name = name.value.trim()
    if (resellerPhone.valid.value) Object.assign(body, resellerPhone.payload())
    const res = await api.call('POST', 'resellers', body)
    created.value = res?.data?.data ?? null
    emit('created', created.value)
    step.value = 'members'
  } catch (e) {
    error.value = serverError(e, 'Could not add reseller.')
  } finally {
    saving.value = false
  }
}

const addMember = async () => {
  if (!memberPhone.valid.value || saving.value) return
  error.value = ''
  saving.value = true
  try {
    const res = await api.call('POST', `resellers/${created.value.id}/members`, {
      name: memberName.value.trim(),
      ...memberPhone.payload()
    })
    created.value = res?.data?.data ?? created.value
    memberName.value = ''
    memberPhone.clear()
  } catch (e) {
    error.value = serverError(e, 'Could not add team member.')
  } finally {
    saving.value = false
  }
}

const close = () => emit('close')

const done = () => {
  if (created.value) router.push(resellerPath(created.value.id))
  close()
}

watch(
  () => props.open,
  open => {
    if (open) return
    step.value = 'reseller'
    name.value = ''
    created.value = null
    memberName.value = ''
    error.value = ''
  }
)

return (_ctx, _cache) => {
  return (__props.open)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "fixed inset-0 z-40 flex items-center justify-center bg-black/40",
        onClick: _withModifiers(close, ["self"])
      }, [
        _createElementVNode("div", _hoisted_1, [
          (step.value === 'reseller')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _cache[6] || (_cache[6] = _createElementVNode("h2", { class: "text-lg font-semibold mb-1" }, "Add a reseller", -1)),
                _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-sm text-gray-500 mb-4" }, " Give the reseller a name. Add a phone number if one person should run it; otherwise add team members on the next step. ", -1)),
                _createVNode(InputField, {
                  modelValue: name.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((name).value = $event)),
                  name: "Name",
                  "data-test": "reseller-name",
                  class: "w-full"
                }, null, 8, ["modelValue"]),
                _createElementVNode("div", {
                  ref_key: "resellerPhoneBox",
                  ref: resellerPhoneBox,
                  class: "mt-4"
                }, [
                  _createVNode(InputField, {
                    modelValue: _unref(resellerPhone).phone.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((_unref(resellerPhone).phone.value) = $event)),
                    name: "Phone number (optional)",
                    type: "tel",
                    class: "w-full"
                  }, null, 8, ["modelValue"])
                ], 512),
                (error.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(error.value), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(NeueButton, {
                    type: "secondary",
                    onClick: close
                  }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(NeueButton, {
                    type: canCreate.value ? 'primary' : 'inactive',
                    loading: saving.value,
                    "data-test": "create-reseller",
                    onClick: createReseller
                  }, {
                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                      _createTextVNode(" Add reseller ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["type", "loading"])
                ])
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[10] || (_cache[10] = _createElementVNode("h2", { class: "text-lg font-semibold mb-1" }, "Add team members", -1)),
                _createElementVNode("p", _hoisted_4, _toDisplayString(createdName.value) + " is ready. Team members log in with their own phone number and share its customers and orders. You can add more later from the reseller's page. ", 1),
                (members.value.length)
                  ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(members.value, (member) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: member.id,
                          class: "py-2 text-sm flex items-center gap-2"
                        }, [
                          _createElementVNode("span", _hoisted_6, _toDisplayString(member.display_name), 1),
                          (member.is_primary)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Primary"))
                            : _createCommentVNode("", true),
                          _createElementVNode("span", _hoisted_8, _toDisplayString(member.phone_number), 1)
                        ]))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(InputField, {
                  modelValue: memberName.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((memberName).value = $event)),
                  name: "Name",
                  "data-test": "member-name",
                  class: "w-full"
                }, null, 8, ["modelValue"]),
                _createElementVNode("div", {
                  ref_key: "memberPhoneBox",
                  ref: memberPhoneBox,
                  class: "mt-4"
                }, [
                  _createVNode(InputField, {
                    modelValue: _unref(memberPhone).phone.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => ((_unref(memberPhone).phone.value) = $event)),
                    name: "Phone number",
                    type: "tel",
                    class: "w-full"
                  }, null, 8, ["modelValue"])
                ], 512),
                (error.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(error.value), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(NeueButton, {
                    type: _unref(memberPhone).valid.value ? 'secondary' : 'inactive',
                    loading: saving.value,
                    "data-test": "add-member",
                    onClick: addMember
                  }, {
                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                      _createTextVNode(" Add team member ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["type", "loading"]),
                  _createVNode(NeueButton, {
                    "data-test": "done",
                    onClick: done
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode("Done", -1)
                    ]))]),
                    _: 1
                  })
                ])
              ], 64))
        ])
      ]))
    : _createCommentVNode("", true)
}
}

}